import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Plus, Trash2, Edit2, X, Phone, Mail, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';

// Lead pipeline for a property (e.g. Adam's Way).
// Shows funnel counts, a full table, add/edit/delete.
// Each lead can optionally be linked to a specific unit.

const STAGES = [
  { key: 'inquired', label: 'Inquired', color: '#64748b' },
  { key: 'toured', label: 'Toured', color: '#3b82f6' },
  { key: 'offer', label: 'Offer', color: '#eab308' },
  { key: 'under_contract', label: 'Under Contract', color: '#f97316' },
  { key: 'closed_won', label: 'Closed · Won', color: '#22c55e' },
  { key: 'closed_lost', label: 'Closed · Lost', color: '#ef4444' },
];
const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.key, s]));

const INTEREST_OPTIONS = [
  { key: 'any', label: 'Any' },
  { key: 'sale', label: 'Sale' },
  { key: 'rental', label: 'Rental' },
  { key: 'commercial', label: 'Commercial' },
];

const emptyForm = () => ({
  name: '', email: '', phone: '',
  unit_id: '', interest: 'any', stage: 'inquired',
  source: '', budget: '', next_followup: '', notes: '',
});

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return v > 0 ? `$${v.toFixed(0)}` : '—';
};

export default function LeadsPanel({ api, propertyId, units, onOpenUnit }) {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');  // 'all' | stage key
  const [unitFilter, setUnitFilter] = useState('');

  const unitMap = useMemo(() => {
    const m = {};
    units.forEach(u => { m[u.id] = u; });
    return m;
  }, [units]);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [lRes, fRes] = await Promise.all([
        api.get(`/properties/${propertyId}/leads`),
        api.get(`/properties/${propertyId}/leads/funnel`),
      ]);
      setLeads(lRes.data);
      setFunnel(fRes.data);
    } catch (e) { toast.error('Failed to load leads'); }
    setLoading(false);
  }, [api, propertyId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (defaults = {}) => {
    setEditing(null);
    setForm({ ...emptyForm(), ...defaults });
    setDialogOpen(true);
  };
  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      unit_id: lead.unit_id || '',
      interest: lead.interest || 'any',
      stage: lead.stage || 'inquired',
      source: lead.source || '',
      budget: lead.budget ?? '',
      next_followup: lead.next_followup || '',
      notes: lead.notes || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      budget: form.budget === '' ? null : Number(form.budget),
    };
    try {
      if (editing) {
        await api.put(`/properties/${propertyId}/leads/${editing.id}`, payload);
        toast.success('Lead updated');
      } else {
        await api.post(`/properties/${propertyId}/leads`, payload);
        toast.success('Lead added');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const del = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    try {
      await api.delete(`/properties/${propertyId}/leads/${lead.id}`);
      toast.success('Lead deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const advanceStage = async (lead, newStage) => {
    if (newStage === lead.stage) return;
    try {
      await api.put(`/properties/${propertyId}/leads/${lead.id}`, { stage: newStage });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (filter !== 'all' && l.stage !== filter) return false;
      if (unitFilter && l.unit_id !== unitFilter) return false;
      return true;
    });
  }, [leads, filter, unitFilter]);

  if (loading) return <div className="ceo-card-elevated p-6 text-center opacity-70">Loading leads…</div>;

  return (
    <div className="ceo-card-elevated p-5 space-y-4" data-testid="leads-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" style={{ color: 'var(--accent)' }} />
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Leads Pipeline</h3>
          {funnel && (
            <span className="opacity-60 text-xs" style={{ color: 'var(--text)' }}>
              · {funnel.total} total
              {funnel.total_budget > 0 && ` · ${fmtMoney(funnel.total_budget)} pipeline`}
            </span>
          )}
        </div>
        <button onClick={() => openCreate()} className="ceo-pill flex items-center gap-1" data-testid="add-lead-btn">
          <Plus className="h-3.5 w-3.5" /> Add Lead
        </button>
      </div>

      {/* Funnel */}
      {funnel && funnel.total > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="lead-funnel">
          {STAGES.map(s => {
            const count = funnel.counts?.[s.key] || 0;
            const pct = funnel.total ? Math.round((count / funnel.total) * 100) : 0;
            const active = filter === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(active ? 'all' : s.key)}
                className="rounded-lg border p-2 text-left transition-all hover:bg-white/5"
                style={{
                  borderColor: active ? s.color : 'rgba(255,255,255,0.1)',
                  background: active ? `${s.color}22` : 'rgba(255,255,255,0.02)',
                }}
                data-testid={`funnel-stage-${s.key}`}>
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text)' }}>{s.label}</span>
                </div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold font-mono" style={{ color: s.color }}>{count}</span>
                  {funnel.total > 0 && (
                    <span className="text-[10px] opacity-60" style={{ color: 'var(--text)' }}>{pct}%</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="opacity-60 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text)' }}>
          Showing {filtered.length}
        </span>
        {filter !== 'all' && (
          <button onClick={() => setFilter('all')} className="ceo-pill text-[11px]" data-testid="clear-stage-filter">
            Stage: {STAGE_MAP[filter]?.label} ×
          </button>
        )}
        {units.length > 0 && (
          <select
            value={unitFilter}
            onChange={e => setUnitFilter(e.target.value)}
            className="bg-transparent border border-white/10 rounded px-2 py-1 text-xs"
            style={{ color: 'var(--text)' }}
            data-testid="unit-filter">
            <option value="" style={{ color: '#000' }}>All units</option>
            {units.map(u => (
              <option key={u.id} value={u.id} style={{ color: '#000' }}>
                {u.unit_code}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p className="text-sm text-center py-8 opacity-60" style={{ color: 'var(--text)' }}>
          {leads.length === 0 ? 'No leads yet. Click "Add Lead" to track your first prospect.' : 'No leads match your filters.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="leads-table">
            <thead>
              <tr className="text-left opacity-60" style={{ color: 'var(--text)' }}>
                <th className="pb-1 pr-2">Name</th>
                <th className="pb-1 pr-2">Contact</th>
                <th className="pb-1 pr-2">Interest</th>
                <th className="pb-1 pr-2">Unit</th>
                <th className="pb-1 pr-2">Stage</th>
                <th className="pb-1 pr-2">Budget</th>
                <th className="pb-1 pr-2">Follow-up</th>
                <th className="pb-1 pr-2">Notes</th>
                <th className="pb-1"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => {
                const stage = STAGE_MAP[l.stage] || STAGE_MAP.inquired;
                const unit = l.unit_id ? unitMap[l.unit_id] : null;
                return (
                  <tr key={l.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`lead-row-${l.id}`}>
                    <td className="py-1.5 pr-2 font-semibold" style={{ color: 'var(--text)' }}>{l.name}</td>
                    <td className="py-1.5 pr-2 opacity-80" style={{ color: 'var(--text)' }}>
                      <div className="flex flex-col">
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:underline">
                            <Mail className="h-3 w-3" /> {l.email}
                          </a>
                        )}
                        {l.phone && (
                          <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:underline">
                            <Phone className="h-3 w-3" /> {l.phone}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 pr-2 opacity-80 capitalize" style={{ color: 'var(--text)' }}>{l.interest || '—'}</td>
                    <td className="py-1.5 pr-2">
                      {unit ? (
                        <button
                          onClick={() => onOpenUnit?.(unit)}
                          className="font-mono font-bold hover:underline"
                          style={{ color: 'var(--accent)' }}
                          title="Open unit">
                          {unit.unit_code}
                        </button>
                      ) : (
                        <span className="opacity-40" style={{ color: 'var(--text)' }}>—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={l.stage}
                        onChange={e => advanceStage(l, e.target.value)}
                        className="bg-transparent rounded px-1.5 py-0.5 text-[11px] border"
                        style={{ color: stage.color, borderColor: stage.color, background: `${stage.color}15` }}
                        data-testid={`stage-select-${l.id}`}>
                        {STAGES.map(s => (
                          <option key={s.key} value={s.key} style={{ color: '#000' }}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2 font-mono" style={{ color: 'var(--text)' }}>{fmtMoney(l.budget)}</td>
                    <td className="py-1.5 pr-2 opacity-80" style={{ color: 'var(--text)' }}>{l.next_followup || '—'}</td>
                    <td className="py-1.5 pr-2 max-w-[240px] truncate opacity-80" style={{ color: 'var(--text)' }} title={l.notes || ''}>{l.notes || '—'}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(l)} className="opacity-70 hover:opacity-100 px-1" data-testid={`edit-lead-${l.id}`}>
                        <Edit2 className="h-3 w-3" style={{ color: 'var(--text)' }} />
                      </button>
                      <button onClick={() => del(l)} className="opacity-70 hover:opacity-100 px-1" data-testid={`delete-lead-${l.id}`}>
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDialogOpen(false)}>
          <div className="ceo-card-elevated p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>{editing ? 'Edit Lead' : 'Add Lead'}</h3>
              <button onClick={() => setDialogOpen(false)}><X className="h-4 w-4" style={{ color: 'var(--text)' }} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name *">
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}
                  data-testid="lead-name-input" />
              </Field>
              <Field label="Source">
                <input type="text" value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  placeholder="website, referral, walk-in…" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }} />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}
                  data-testid="lead-email-input" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }} />
              </Field>
              <Field label="Interest">
                <select value={form.interest} onChange={e => setForm(f => ({ ...f, interest: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}>
                  {INTEREST_OPTIONS.map(o => <option key={o.key} value={o.key} style={{ color: '#000' }}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Stage">
                <select value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}
                  data-testid="lead-stage-input">
                  {STAGES.map(s => <option key={s.key} value={s.key} style={{ color: '#000' }}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Linked Unit">
                <select value={form.unit_id} onChange={e => setForm(f => ({ ...f, unit_id: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }}
                  data-testid="lead-unit-input">
                  <option value="" style={{ color: '#000' }}>— none —</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id} style={{ color: '#000' }}>
                      {u.unit_code} · {u.type}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Budget (USD)">
                <input type="number" step="1000" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))}
                  placeholder="e.g. 600000" className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm font-mono" style={{ color: 'var(--text)' }} />
              </Field>
              <Field label="Next Follow-up">
                <input type="date" value={form.next_followup} onChange={e => setForm(f => ({ ...f, next_followup: e.target.value }))}
                  className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm" style={{ color: 'var(--text)' }} />
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Touring A-302 next Saturday, needs parking, etc."
                    className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm min-h-[80px]" style={{ color: 'var(--text)' }} />
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDialogOpen(false)} className="ceo-pill">Cancel</button>
              <button onClick={save} disabled={saving} className="ceo-pill" style={{ background: 'var(--accent)', color: '#000' }} data-testid="lead-save-btn">
                {saving ? 'Saving…' : editing ? 'Update' : 'Add Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider opacity-70 mb-1" style={{ color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}
